import type { TextInputInstance } from 'react-native';
import React, { useState, useEffect, useRef } from 'react';
import {
    View,
    Text,
    TextInput,
    TouchableOpacity,
    StyleSheet,
    ScrollView,
    ActivityIndicator,
    Platform,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { getCategoriesForPicker, getPickerSources, type SourceId } from '../../lib/categoryService';
import type { Category } from '../../lib/database.client.types';
import CategorySourceTabs from './CategorySourceTabs';
import {
    canAccessPubmed,
    fetchCurrentUserSourceAccess,
    PUBMED_SOURCE_ID,
    type SourceAccessDecision,
} from '../../lib/sourceAccess';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

interface CategoryPickerProps {
    selectedCategories: Category[];
    onCategoryAdd: (category: Category) => void;
    onCategoryRemove: (categoryId: number) => void;
    onDropdownChange?: (isOpen: boolean) => void;
    disabled?: boolean;
    projectId?: string | null;
}

export default function CategoryPicker({
    selectedCategories,
    onCategoryAdd,
    onCategoryRemove,
    onDropdownChange,
    disabled = false,
    projectId = null,
}: CategoryPickerProps) {
    const { i18n } = useLingui();
    const [selectedSource, setSelectedSource] = useState<SourceId>(1); // Default to arXiv
    const [searchTerm, setSearchTerm] = useState('');
    const [allCategories, setAllCategories] = useState<Category[]>([]);
    const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showDropdown, setShowDropdown] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const inputRef = useRef<TextInputInstance>(null);
    const dropdownItemRefs = useRef<Map<number, any>>(new Map());
    const [sourceAccess, setSourceAccess] = useState<SourceAccessDecision[]>([]);
    const [sourceAccessLoading, setSourceAccessLoading] = useState(true);
    const [sourceAccessFailed, setSourceAccessFailed] = useState(false);
    const pickerSources = getPickerSources(sourceAccess);
    const pubmedAllowed = canAccessPubmed(sourceAccess);
    const selectedSourceAllowed = selectedSource !== PUBMED_SOURCE_ID
        || pubmedAllowed;

    useEffect(() => {
        let active = true;
        setSourceAccessLoading(true);
        setSourceAccessFailed(false);
        fetchCurrentUserSourceAccess(projectId)
            .then((decisions) => {
                if (active) setSourceAccess(decisions);
            })
            .catch((error) => {
                console.error('Failed to load source access:', error);
                if (active) {
                    setSourceAccess([]);
                    setSourceAccessFailed(true);
                }
            })
            .finally(() => {
                if (active) setSourceAccessLoading(false);
            });
        return () => {
            active = false;
        };
    }, [projectId]);

    // Load categories when component mounts or source changes
    useEffect(() => {
        const loadCategories = async () => {
            if (!selectedSourceAllowed) {
                setAllCategories([]);
                setFilteredCategories([]);
                setSearchTerm('');
                setShowDropdown(false);
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            try {
                const categories = await getCategoriesForPicker(selectedSource);
                setAllCategories(categories);
                setSearchTerm(''); // Reset search when changing source
                setFilteredCategories([]);
                setShowDropdown(false);
            } catch (err) {
                console.error('Error loading categories for source:', selectedSource, err);
                setAllCategories([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadCategories();
    }, [selectedSource, selectedSourceAllowed]);

    // Auto-focus search input when categories are loaded (web only)
    useEffect(() => {
        if (Platform.OS === 'web' && !disabled && !isLoading && allCategories.length > 0) {
            // Small delay to ensure the input is fully rendered and focusable
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [disabled, isLoading, allCategories.length]);

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = allCategories.filter(cat =>
                cat.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                cat.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                cat.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredCategories(filtered.slice(0, 10)); // Limit to 10 results
            setShowDropdown(true);
            setHighlightedIndex(0); // Reset highlight when results change
        } else {
            setFilteredCategories([]);
            setShowDropdown(false);
            setHighlightedIndex(0);
        }
    }, [searchTerm, allCategories]);

    useEffect(() => {
        onDropdownChange?.(showDropdown);
    }, [showDropdown, onDropdownChange]);

    // Scroll highlighted item into view (web only)
    useEffect(() => {
        if (Platform.OS === 'web' && showDropdown && highlightedIndex >= 0) {
            const element = dropdownItemRefs.current.get(highlightedIndex);
            if (element) {
                element.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
            }
        }
    }, [highlightedIndex, showDropdown]);

    const handleCategorySelect = (category: Category) => {
        onCategoryAdd(category);
        setSearchTerm('');
        setShowDropdown(false);
        setHighlightedIndex(0);
    };

    // Keyboard navigation handler (web only)
    const handleKeyDown = (e: any) => {
        if (Platform.OS !== 'web' || !showDropdown || filteredCategories.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => {
                    // Skip already selected categories
                    let nextIndex = (prev + 1) % filteredCategories.length;
                    while (selectedCategories.some(s => s.id === filteredCategories[nextIndex].id) && nextIndex !== prev) {
                        nextIndex = (nextIndex + 1) % filteredCategories.length;
                    }
                    return nextIndex;
                });
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => {
                    // Skip already selected categories
                    let nextIndex = prev - 1;
                    if (nextIndex < 0) nextIndex = filteredCategories.length - 1;
                    while (selectedCategories.some(s => s.id === filteredCategories[nextIndex].id) && nextIndex !== prev) {
                        nextIndex = nextIndex - 1;
                        if (nextIndex < 0) nextIndex = filteredCategories.length - 1;
                    }
                    return nextIndex;
                });
                break;
            case 'Enter': {
                e.preventDefault();
                const highlightedCategory = filteredCategories[highlightedIndex];
                if (highlightedCategory && !selectedCategories.some(s => s.id === highlightedCategory.id)) {
                    handleCategorySelect(highlightedCategory);
                }
                break;
            }
            case 'Escape':
                e.preventDefault();
                setShowDropdown(false);
                setSearchTerm('');
                break;
        }
    };

    return (
        <View style={styles.container}>
            <CategorySourceTabs
                selectedSource={selectedSource}
                onSelectSource={setSelectedSource}
                disabled={disabled}
                decisions={sourceAccess}
                pubmedAllowed={pubmedAllowed}
                accessLoading={sourceAccessLoading}
                accessFailed={sourceAccessFailed}
            />

            {selectedCategories.length > 0 && (
                <View style={styles.pillContainer}>
                    {selectedCategories.map(cat => {
                        const categoryLocked = cat.source_id === PUBMED_SOURCE_ID
                            && !pubmedAllowed;
                        const sourceName = cat.source_id ? pickerSources[cat.source_id]?.displayName || (cat.oai_spec?.startsWith('iacr:') ? 'IACR ePrint' : null) : null;
                        const categoryDisplay = cat.source_id === 1 && cat.name
                            ? `${cat.name} (${cat.code})`
                            : (cat.name || cat.code);
                        const displayText = cat.source_id !== 1 && sourceName
                            ? `${sourceName}: ${categoryDisplay}`
                            : categoryDisplay;

                        return (
                            <View key={cat.id.toString()} style={styles.pill}>
                                <Text style={styles.pillText}>
                                    {displayText}
                                </Text>
                                <TouchableOpacity
                                    onPress={() => onCategoryRemove(cat.id)}
                                    disabled={disabled || categoryLocked}
                                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                                >
                                    <MaterialIcons name={categoryLocked ? 'lock' : 'close'} size={16} color="#6b7280" />
                                </TouchableOpacity>
                            </View>
                        );
                    })}
                </View>
            )}

            <View style={styles.searchContainer}>
                {selectedSourceAllowed && (
                <TextInput
                    ref={inputRef}
                    style={[
                        styles.searchInput,
                        isLoading && styles.searchInputLoading
                    ].filter(Boolean)}
                    placeholder={isLoading
                        ? i18n._(msg({ id: 'categoryPicker.loadingPlaceholder', message: 'Loading categories...' }))
                        : i18n._(msg({ id: 'categoryPicker.searchPlaceholder', message: 'Type to search categories...' }))}
                    placeholderTextColor={isLoading ? "#d1d5db" : "#9ca3af"}
                    value={searchTerm}
                    onChangeText={setSearchTerm}
                    editable={!disabled && !isLoading && selectedSourceAllowed}
                    autoFocus={Platform.OS === 'web' && !isLoading}
                    onKeyPress={Platform.OS === 'web' ? handleKeyDown : undefined}
                />
                )}
                {isLoading && (
                    <ActivityIndicator
                        size="small"
                        color="#9ca3af"
                        style={styles.loader}
                    />
                )}
                
                {showDropdown && filteredCategories.length > 0 && (
                    <View style={styles.dropdown}>
                        <ScrollView
                            style={styles.dropdownScroll}
                            keyboardShouldPersistTaps="handled"
                        >
                            {filteredCategories.map((cat, index) => {
                                const isSelected = selectedCategories.some(s => s.id === cat.id);
                                const isHighlighted = index === highlightedIndex;
                                return (
                                    <TouchableOpacity
                                        key={cat.id.toString()}
                                        ref={(el) => {
                                            if (el) {
                                                dropdownItemRefs.current.set(index, el);
                                            } else {
                                                dropdownItemRefs.current.delete(index);
                                            }
                                        }}
                                        style={[
                                            styles.dropdownItem,
                                            isSelected && styles.dropdownItemSelected,
                                            isHighlighted && !isSelected && styles.dropdownItemHighlighted,
                                        ].filter(Boolean)}
                                        onPress={() => !isSelected && handleCategorySelect(cat)}
                                        {...(Platform.OS === 'web' && {
                                            onMouseEnter: () => setHighlightedIndex(index)
                                        } as any)}
                                        disabled={isSelected || disabled}
                                    >
                                        <Text style={[
                                            styles.dropdownItemText,
                                            isSelected && styles.dropdownItemTextSelected,
                                        ].filter(Boolean)}>
                                            {cat.source_id === 1 && cat.name ? `${cat.name} (${cat.code})` : (cat.name || cat.code)}
                                        </Text>
                                        {cat.description && (
                                            <Text style={styles.dropdownItemDescription}>
                                                {cat.description}
                                            </Text>
                                        )}
                                    </TouchableOpacity>
                                );
                            })}
                        </ScrollView>
                    </View>
                )}

                {showDropdown && filteredCategories.length === 0 && searchTerm.trim() && (
                    <View style={styles.dropdown}>
                        <Text style={styles.noResults}><Trans id="discovery.categorypicker.noCategoriesFound" comment="UI copy in components/discovery/CategoryPicker.tsx">No categories found</Trans></Text>
                    </View>
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        position: 'relative',
    },
    pillContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 16,
        marginBottom: 6,
    },
    pill: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#f8fafc',
        borderWidth: 1,
        borderColor: '#e2e8f0',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
        gap: 6,
        maxWidth: '100%',
        flexShrink: 1,
    },
    pillText: {
        color: '#334155',
        fontSize: 12,
        flexShrink: 1,
    },
    searchContainer: {
        position: 'relative',
    },
    searchInput: {
        borderWidth: 1,
        borderColor: '#e5e7eb',
        borderRadius: 8,
        padding: 12,
        fontSize: 16,
        backgroundColor: 'white',
        marginTop: 8,
    },
    searchInputLoading: {
        backgroundColor: '#f9fafb',
        color: '#9ca3af',
    },
    loader: {
        position: 'absolute',
        right: 12,
        top: '50%',
        marginTop: -10,
    },
    dropdown: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        marginTop: 4,
        backgroundColor: 'white',
        borderWidth: 1,
        borderColor: '#e5e7eb',
        borderRadius: 8,
        maxHeight: 300,
        ...(Platform.OS === 'web' && {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        }),
        ...(Platform.OS === 'ios' && {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 3,
        }),
        ...(Platform.OS === 'android' && {
            elevation: 3,
        }),
    },
    dropdownScroll: {
        maxHeight: 300,
    },
    dropdownItem: {
        padding: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#f3f4f6',
    },
    dropdownItemSelected: {
        backgroundColor: '#f9fafb',
    },
    dropdownItemHighlighted: {
        backgroundColor: '#e0f2fe',
    },
    dropdownItemText: {
        fontSize: 16,
        color: '#111827',
    },
    dropdownItemTextSelected: {
        color: '#9ca3af',
    },
    dropdownItemDescription: {
        fontSize: 12,
        color: '#6b7280',
        marginTop: 4,
    },
    noResults: {
        padding: 12,
        color: '#6b7280',
        textAlign: 'center',
    },
});
